import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetMyQuotes } from '~/api/requestForQuote';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { TabList, type TabDescriptor } from '~/components/ui/TabList';
import { tabButtonId, tabPanelId } from '~/components/ui/tabIds';
import { PerspectiveCaption } from '~/components/ui/PerspectiveCaption';
import type { MyQuotesQuery } from '~/graphql/graphql';
import { MyQuotesList } from '~/pages/my-quotes/sections/MyQuotesList';
import type { MyQuoteRow } from '~/pages/my-quotes/sections/MyQuotesList';

type Tab = 'pending' | 'responded';

const tabOrder: Tab[] = ['pending', 'responded'];

export default function MyQuotes() {
  const { t } = useTranslation('my-quotes');
  const tabsId = useId();
  const [activeTab, setActiveTab] = useState<Tab>('pending');

  const list = usePaginatedQuery<MyQuotesQuery, MyQuoteRow, boolean>({
    fetchHook: useGetMyQuotes,
    select: (data) => data?.myQuotes,
    options: { filters: activeTab === 'pending' },
  });

  const tabLabels: Record<Tab, string> = {
    pending: t('my_quotes.tabs.pending'),
    responded: t('my_quotes.tabs.responded'),
  };

  const tabs: TabDescriptor<Tab>[] = tabOrder.map((tab) => ({
    id: tab,
    label: tabLabels[tab],
    count: tab === activeTab ? list.pagination?.totalElements : undefined,
    testId: `my-quotes-tab-${tab}`,
  }));

  return (
    <div className="w-full flex flex-col gap-8">
      <title>{t('my_quotes.title')}</title>
      <div>
        <h1 className="text-2xl font-semibold text-neut-prim">
          {t('my_quotes.title')}
        </h1>
        <PerspectiveCaption perspective="SELLING" />
      </div>

      <TabList
        baseId={tabsId}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        ariaLabel={t('my_quotes.tabs_label')}
      />

      <div
        role="tabpanel"
        id={tabPanelId(tabsId)}
        aria-labelledby={tabButtonId(tabsId, activeTab)}
      >
        <MyQuotesList
          quotes={list.data}
          isLoading={list.isLoading}
          isError={Boolean(list.error)}
          onRetry={() => void list.refetch()}
          sentinelRef={list.sentinelRef}
          isLoadingMore={list.isLoadingMore}
          hasMoreData={list.hasMoreData}
          totalCount={list.pagination?.totalElements ?? 0}
        />
      </div>
    </div>
  );
}
